import { ReactElement } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'

import { Column } from '@10xLabs/react-web/components/Column'
import { Row } from '@10xLabs/react-web/components/Row'
import Heading from 'components/v2/Heading'
import { InputDate } from 'components/v2/InputDate'
import { IPlanningItemProps, IStopBulletProps } from './types'
import { LocationIcon } from 'components/Icons/LocationIcon'
import { device } from 'utils/style'

const INPUT_DATE_INDEX = 2

const StyledDatePicker = styled(InputDate)<TSFixMe>``

const PlanningItemContainer = styled.div<{ isActive?: boolean; isLastSingleItem?: boolean }>`
  background-color: #ffffff;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding: 48px;

  ${StyledDatePicker} {
    width: 50%;
  }

  &:hover {
    background-color: #e0d6ff33;
    border: 1px solid #916ee8;

    ${StyledDatePicker} {
      & > div:nth-child(${INPUT_DATE_INDEX}) {
        background-color: #e0d6ff4d;
        border: 1px solid #e3e3e3;

        input {
          color: #423f9a;
        }

        svg {
          fill: #916ee8;
        }
      }
    }
  }

  @media ${device.laptop} {
    grid-column: ${({ isLastSingleItem }): string => (isLastSingleItem ? 'span 2' : 'span 1')};
  }
`

const StopsContainer = styled.div<TSFixMe>`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StopContainer = styled.div<TSFixMe>`
  align-items: center;
  display: flex;
  gap: 24px;
  text-transform: capitalize;
`

const BulletContainer = styled.div<TSFixMe>`
  align-items: center;
  display: flex;
  justify-content: center;
  position: relative;
  height: 30px;
  width: 30px;
`

const OriginBullet = styled.div<TSFixMe>`
  background-color: #ffffff;
  border: 4px solid #807fb9;
  border-radius: 50%;
  height: 9px;
  width: 9px;
  z-index: 1;
`

const StopBullet = styled.div<TSFixMe>`
  background-color: #807fb9;
  border: 2px solid #ffffff;
  border-radius: 50%;
  height: 5px;
  width: 5px;
  z-index: 1;
`

const DestinationBullet = styled(LocationIcon)<TSFixMe>`
  fill: #807fb9;
  height: 25px;
  width: 25px;
  z-index: 1;
`

const PlaceLine = styled.div<{ isOrigin?: boolean; isDestination?: boolean }>`
  background-color: #807fb9;
  bottom: ${({ isDestination }): string => (isDestination ? '27.5px' : 'auto')};
  height: ${({ isOrigin, isDestination }): string => (isOrigin || isDestination ? '20px' : '50px')};
  position: absolute;
  top: ${({ isOrigin }): string => (isOrigin ? '27.5px' : 'auto')};
  width: 1px;
`

const messages = defineMessages({
  startDateLabel: {
    id: 'services.planning.startDateLabel',
    defaultMessage: 'Start date'
  },
  endDateLabel: {
    id: 'services.planning.endDateLabel',
    defaultMessage: 'End date'
  }
})

const PlaceBullet = ({ isOrigin, isDestination }: IStopBulletProps): ReactElement => {
  let content = <></>
  if (isOrigin) {
    content = <OriginBullet />
  } else if (isDestination) {
    content = <DestinationBullet />
  } else {
    content = <StopBullet />
  }

  return (
    <BulletContainer>
      {content}
      <PlaceLine isOrigin={isOrigin} isDestination={isDestination} />
    </BulletContainer>
  )
}

export const PlanningItem = ({
  label,
  startDate,
  endDate,
  stops,
  onClick,
  isLastSingleItem
}: IPlanningItemProps): ReactElement => {
  const intl = useIntl()

  const handleClickPlanningItem = (): void => {
    onClick()
  }

  return (
    <PlanningItemContainer onClick={handleClickPlanningItem} isLastSingleItem={isLastSingleItem}>
      <Heading.H4>{label}</Heading.H4>
      <Row>
        <Column>
          <StyledDatePicker
            intl={intl}
            label={intl.formatMessage(messages.startDateLabel)}
            value={startDate}
            onChange={(): void => undefined}
            disabled
          />
        </Column>
        <Column>
          <StyledDatePicker
            intl={intl}
            label={intl.formatMessage(messages.endDateLabel)}
            placeholder="--/--/----"
            value={endDate}
            onChange={(): void => undefined}
            disabled
          />
        </Column>
      </Row>
      <StopsContainer>
        {stops.map((stop, index): TSFixMe => {
          const isFirstStop = index === 0
          const isLastStop = index === stops.length - 1

          return (
            <StopContainer key={stop.name}>
              <PlaceBullet isOrigin={isFirstStop} isDestination={isLastStop} />
              {isFirstStop || isLastStop ? (
                <Heading.H5>{stop.name}</Heading.H5>
              ) : (
                <Heading.Body>{stop.name}</Heading.Body>
              )}
            </StopContainer>
          )
        })}
      </StopsContainer>
    </PlanningItemContainer>
  )
}
